/**
 * Server-rendered menu block on the public place page.
 *
 * Everything here comes from loader data, so the menu link, PDF and menu photo
 * are present in the first HTML response — crawlable by Google and AI search
 * engines instead of being fetched after hydration.
 */
import { ExternalLink, FileText, UtensilsCrossed } from "lucide-react";
import { SmartImage } from "@/components/ui/SmartImage";
import { dietBadge, formatDishPrice, groupMenuDishes, hasPlaceMenu, type MenuDish, type PlaceMenu } from "@/lib/place-menu";
import {
  availabilityLabel,
  isMenuAvailableNow,
  menuHref,
  menuIsImage,
  menuIsPdf,
  type PlaceMenuDoc,
} from "@/lib/place-menus";

export function PlaceMenuSection({
  menu,
  menus = [],
  placeName,
  dishes = [],
  lang = "en",
}: {
  menu: PlaceMenu | null | undefined;
  menus?: PlaceMenuDoc[];
  placeName: string;
  dishes?: MenuDish[];
  lang?: "en" | "fi";
}) {
  const visibleDishes = (dishes ?? []).filter((d) => d.isActive && d.name.trim());
  const docs = (menus ?? []).filter((d) => d.isActive && menuHref(d));
  if (!hasPlaceMenu(menu) && visibleDishes.length === 0 && docs.length === 0) return null;
  const m = (menu ?? { url: null, pdf: null, image: null, updatedAt: null }) as PlaceMenu;
  const groups = groupMenuDishes(visibleDishes);

  const L =
    lang === "fi"
      ? {
          heading: "Ruokalista",
          intro: `${placeName} — ajantasainen ruokalista.`,
          openMenu: "Avaa ruokalista",
          openPdf: "Avaa ruokalista (PDF)",
          alt: `${placeName} ruokalista`,
          updated: "Päivitetty",
          noDishes: "Annoslistaa ei ole vielä lisätty — avaa koko ruokalista alta.",
          sourceLabel: "Ravintolan oma ruokalista",
        }
      : {
          heading: "Menu",
          intro: `${placeName} — current menu.`,
          openMenu: "Open menu",
          openPdf: "Open menu (PDF)",
          alt: `${placeName} menu`,
          updated: "Updated",
          noDishes: "Dish list not added yet — open the full menu below.",
          sourceLabel: "Menu from the restaurant",

        };

  const updated = m.updatedAt ? new Date(m.updatedAt) : null;

  const docHrefs = new Set(docs.map((d) => menuHref(d)).filter(Boolean) as string[]);
  const showUrl = Boolean(m.url && !docHrefs.has(m.url));
  const showPdf = Boolean(m.pdf && !docHrefs.has(m.pdf));
  const showImage = Boolean(m.image && !docHrefs.has(m.image));
  const linkHost = (() => {
    const raw = (showUrl ? m.url : showPdf ? m.pdf : null) ?? null;
    if (!raw) return null;
    try {
      return new URL(raw).hostname.replace(/^www\./, "");
    } catch {
      return null;
    }
  })();


  return (
    <section id="menu" className="mt-10 sm:mt-12 max-w-3xl scroll-mt-24">
      <h2 className="font-heading text-navy text-lg sm:text-xl flex items-center gap-2">
        <UtensilsCrossed className="size-5 text-teal" aria-hidden="true" />
        {L.heading}
      </h2>
      <p className="mt-1.5 text-sm text-navy/70">{L.intro}</p>

      <div className="mt-4 rounded-2xl border border-navy/15 bg-white p-4 sm:p-5 space-y-4">
        {groups.length === 0 && docs.length === 0 && !m.image && <p className="text-sm text-navy/60">{L.noDishes}</p>}
        {groups.length > 0 && (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.section || "_"}>
                {group.section && (
                  <h3 className="font-heading text-navy text-base">{group.section}</h3>
                )}
                <ul className="mt-2 divide-y divide-navy/10">
                  {group.dishes.map((dish) => {
                    const price = formatDishPrice(dish.price, dish.currency, lang);
                    return (
                      <li key={dish.id} className="py-3 flex items-start justify-between gap-4">
                        {dish.imageUrl && (
                          <SmartImage
                            src={dish.imageUrl}
                            alt={dish.name}
                            className="size-16 shrink-0 rounded-lg border border-navy/10 object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-navy">{dish.name}</p>
                          {dish.description && (
                            <p className="mt-0.5 text-sm text-navy/70">{dish.description}</p>
                          )}
                          {dish.dietaryTags.length > 0 && (
                            <ul className="mt-1.5 flex flex-wrap gap-1.5">
                              {dish.dietaryTags.map((tag) => {
                                const b = dietBadge(tag, lang);
                                const cls =
                                  b.tone === "veg"
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                    : b.tone === "nonveg"
                                      ? "border-rose-300 bg-rose-50 text-rose-800"
                                      : "border-navy/15 bg-navy/5 text-navy/70";
                                return (
                                  <li
                                    key={tag}
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}
                                  >
                                    {b.label}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                        {price && <span className="shrink-0 text-sm font-bold text-navy">{price}</span>}

                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}

        {docs.length > 0 && (
          <ul className="space-y-2">
            {docs.map((doc) => {
              const href = menuHref(doc);
              if (!href) return null;
              const now = isMenuAvailableNow(doc);
              const when = availabilityLabel(doc);
              const image = menuIsImage(doc);
              return (
                <li key={doc.id} className="rounded-xl border border-navy/10 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-navy">{doc.name}</p>
                      <p style={{ textTransform: "none" }} className="text-[12px] text-navy/55">
                        {when ?? (now ? "Available now" : "Menu")}
                        {doc.priceNote ? ` · ${doc.priceNote}` : ""}
                      </p>
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-royal"
                    >
                      {menuIsPdf(doc) ? <FileText className="size-3.5" /> : <ExternalLink className="size-3.5" />}
                      {L.openMenu}
                    </a>
                  </div>
                  {image ? (
                    <figure className="mt-3 overflow-hidden rounded-lg border border-navy/10 bg-slate-50">
                      <SmartImage src={href} alt={`${placeName} — ${doc.name}`} className="w-full object-contain" />
                    </figure>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {showImage && (
          <figure className="overflow-hidden rounded-xl border border-navy/10 bg-slate-50">
            <SmartImage src={m.image!} alt={L.alt} className="w-full object-contain" />
            <figcaption className="px-3 py-2 text-[12px] text-navy/60">{L.alt}</figcaption>
          </figure>
        )}

        {(showUrl || showPdf) && (
          <div className="rounded-xl border border-navy/10 bg-navy/[0.03] p-4">
            <p style={{ textTransform: "none" }} className="text-[11px] font-bold uppercase tracking-widest text-navy/50">
              {L.sourceLabel}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
              {showUrl && (
                <a
                  href={m.url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition hover:bg-royal"
                >
                  <ExternalLink className="size-4" aria-hidden="true" />
                  {L.openMenu}
                </a>
              )}
              {showPdf && (
                <a
                  href={m.pdf!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-navy transition hover:bg-navy/5"
                >
                  <FileText className="size-4" aria-hidden="true" />
                  {L.openPdf}
                </a>
              )}
              {linkHost && (
                <span style={{ textTransform: "none" }} className="text-[12px] text-navy/55">
                  {linkHost}
                </span>
              )}
            </div>
          </div>
        )}


        {updated && !Number.isNaN(updated.getTime()) && (
          <p className="text-[12px] text-navy/50">
            {L.updated} <time dateTime={updated.toISOString()}>{updated.toLocaleDateString(lang === "fi" ? "fi-FI" : "en-GB")}</time>
          </p>
        )}
      </div>
    </section>
  );
}
